import React, { useCallback, useRef } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  Link,
  Paper,
  Typography as MuiTypography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import MaterialTable from "material-table";
import CopyIcon from "@material-ui/icons/FileCopy";
import { Compare, ExpandMore, Visibility } from "@material-ui/icons";

import { NavLink } from "react-router-dom";
import { useApp } from "../../../../AppProvider";

import {
  copyToClipboard,
  dateFormatter,
  scrollToTarget,
} from "../../../../utils";
import BasicWaterSourceInfo from "./BasicWaterSourceInfo";
import { useWaterSources } from "./useWaterSources";
import WaterSourcesCompare from "./WaterSourcesCompare";
import {
  generalColorMap,
  getColorForValue,
  getTextColor,
  sClassColorMap,
} from "./WaterSourcesConstants";
import PrintRefButton from "../../../../components/graphs/PrintRefButton";
import SaveRefButton from "../../../../components/graphs/SaveRefButton";
import { Alert } from "@material-ui/lab";
import InfoIcon from "@material-ui/icons/Info";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;

function WaterSourcesManagement() {
  const {
    selectedWaterSource,
    setSelectedWaterSource,
    data,
    isFetching,
    error,
    refetch,
    selectedRows,
    handleRowClick,
    selectedWaterSourcesCompare,
    setSelectedWaterSourcesCompare,
  } = useWaterSources();
  const { doToast, currentUser } = useApp();
  const readSaveRef = useRef(null);
  const compareSaveRef = useRef(null);

  const handleScrollToReadSave = useCallback(() => {
    setTimeout(() => scrollToTarget(readSaveRef), 1000);
  }, []);

  const handleScrollToCompareSave = useCallback(() => {
    setTimeout(() => scrollToTarget(compareSaveRef), 1000);
  }, []);

  const WATER_SOURCES_TABLE_COLUMNS = [
    {
      title: "Source Abbrev",
      field: "wadt_source_abbrev",
      cellStyle: {
        width: 210,
        minWidth: 210,
      },
    },
    {
      title: "Class",
      field: "s_class",
      cellStyle: (cellData) => ({
        backgroundColor: getColorForValue(cellData, sClassColorMap),
        color: getTextColor(getColorForValue(cellData, sClassColorMap)), // Set text color based on background
        // Add other cell styles as needed
        width: 120,
        minWidth: 120,
      }),
    },
    {
      title: "Ownership",
      field: "ownership",
      cellStyle: (cellData) => ({
        backgroundColor: getColorForValue(cellData, generalColorMap),
        color: getTextColor(getColorForValue(cellData, sClassColorMap)), // Set text color based on background
        // Add other cell styles as needed
      }),
    },
    {
      title: "Availability",
      field: "availability",
      cellStyle: (cellData) => ({
        backgroundColor: getColorForValue(cellData, generalColorMap),
        color: getTextColor(getColorForValue(cellData, sClassColorMap)), // Set text color based on background
        // Add other cell styles as needed
      }),
    },
    {
      title: "Competition",
      field: "s_competition",
      cellStyle: (cellData) => ({
        backgroundColor: getColorForValue(cellData, generalColorMap),
        color: getTextColor(getColorForValue(cellData, sClassColorMap)), // Set text color based on background
        // Add other cell styles as needed
        width: 170,
        minWidth: 170,
      }),
    },
    {
      title: "Potability",
      field: "s_potability",
      cellStyle: {
        width: 200,
        minWidth: 200,
      },
    },
    {
      title: "WR Type",
      field: "s_wr_type",
      cellStyle: {
        width: 150,
        minWidth: 150,
      },
    },
    {
      title: "Yield, CU (AF/share)",
      field: "yieldcu_afpsh",
      cellStyle: {
        width: 170,
        minWidth: 170,
      },
    },
    {
      title: "Cost ($/AF)",
      field: "cost_per_af",
      cellStyle: {
        width: 160,
        minWidth: 160,
      },
    },
  ];

  // const handleDelete = (oldData) => {
  //   return (async () => {
  //     try {
  //       if (oldData) {
  //         const token = await getAccessTokenSilently();
  //         const headers = { Authorization: `Bearer ${token}` };
  //         await axios.delete(
  //           `${process.env.REACT_APP_ENDPOINT}/api/ui-report-sources/${oldData.wadt_source_ndx}`,
  //           { headers }
  //         );
  //         doToast("success", "This entry was deleted from the database");
  //         if (selectedWaterSource === oldData.wadt_source_ndx) {
  //           setSelectedWaterSource(null);
  //         }
  //
  //         await refetch();
  //       } else {
  //         doToast("error", "Something went wrong");
  //       }
  //     } catch (err) {
  //       console.error(err);
  //       const message = err?.message ?? "Something went wrong";
  //       doToast("error", message);
  //     }
  //   })();
  // };

  if (error) return "An error has occurred: " + error.message;
  return (
    <React.Fragment>
      <Helmet title="WADT Water Source Details Report" />
      <Typography variant="h3" gutterBottom display="inline">
        WADT Water Source Details Report
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to={currentUser?.home || "/"}>
          Dashboard
        </Link>
        <Typography>WADT Water Source Details Report</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <div>
        <Box px={4} my={3} display="flex" alignItems="center">
          <InfoIcon
            style={{ fontSize: 48, marginRight: 16 }}
            color="secondary"
          />{" "}
          {/* Adjust icon size */}
          <div>
            <Alert
              severity="info"
              icon={<Visibility />}
              style={{ display: "flex", alignItems: "center" }}
            >
              <strong>Viewing Source Data</strong> To view the detailed
              information entered for a WADT Water Source, click the "eyeball"
              action-icon to the left of the row.
            </Alert>
            <Alert
              severity="info"
              icon={<Compare />}
              style={{ display: "flex", alignItems: "center" }}
            >
              <strong>Comparing Multiple Sources</strong> Clicking anywhere on a
              row will select a Source for comparison. Select up to 4 sources
              and then click the "compare" action icon which is above the top
              right corner of the table.
            </Alert>
          </div>
        </Box>
      </div>

      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box style={{ width: "100%" }}>
              <Typography variant="h3">Water Sources Selection</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableWrapper>
              <MaterialTable
                id="WADT Water Source Details Report"
                title={`WADT Water Source Details Report ${dateFormatter(
                  new Date(),
                  "MM/DD/YYYY, h:mm A"
                )}`}
                loading={isFetching || !data}
                columns={WATER_SOURCES_TABLE_COLUMNS}
                data={data}
                localization={{
                  toolbar: { searchPlaceholder: "Water Sources Selection" },
                }}
                // editable={{
                //   onRowDelete: handleDelete,
                // }}
                actions={[
                  {
                    icon: CopyIcon,
                    tooltip: "Copy Data",
                    isFreeAction: true,
                    onClick: () => {
                      try {
                        copyToClipboard(data, WATER_SOURCES_TABLE_COLUMNS, () =>
                          doToast(
                            "success",
                            "Data was copied to your clipboard."
                          )
                        );
                      } catch (error) {
                        const message =
                          error?.message ?? "Something went wrong";
                        doToast("error", message);
                      }
                    },
                  },
                  () => ({
                    icon: "visibility",
                    tooltip: "View",
                    onClick: (event, rowData) => {
                      setSelectedWaterSource(rowData?.wadt_source_ndx);
                      setSelectedWaterSourcesCompare([]);
                      handleScrollToReadSave();
                    },
                  }),
                  {
                    icon: "compare",
                    tooltip: "Compare",
                    isFreeAction: true,
                    disabled: selectedRows.length < 2,
                    onClick: () => {
                      setSelectedWaterSourcesCompare(selectedRows);
                      setSelectedWaterSource(null);
                      handleScrollToCompareSave();
                    },
                  },
                  // {
                  //   icon: "add_box",
                  //   tooltip: "Add",
                  //   isFreeAction: true,
                  //   onClick: () => {
                  //     setSelectedWaterSource(0);
                  //   },
                  // },
                ]}
                options={{
                  filtering: true,
                  emptyRowsWhenPaging: false,
                  showTitle: false,
                  columnsButton: true,
                  exportButton: true,
                  exportAllData: true,
                  addRowPosition: "first",
                  pageSize: 50,
                  pageSizeOptions: [10, 30, 50, 100, 200],
                  padding: "dense",
                  searchFieldAlignment: "left",
                  minBodyHeight: "250px",
                  maxBodyHeight:
                    "calc(100vh - 64px - 48px - 106px - 48px - 64px - 204px)",
                  rowStyle: (rowData) => {
                    const index = selectedRows.indexOf(rowData.wadt_source_ndx);
                    if (index === 0) {
                      return {
                        border: "4px solid red", // Red for the first selected row
                      };
                    } else if (index === 1) {
                      return {
                        border: "4px solid blue", // Blue for the second selected row
                      };
                    } else if (index === 2) {
                      return {
                        border: "4px solid orange", // Blue for the second selected row
                      };
                    } else if (index === 3) {
                      return {
                        border: "4px solid green", // Blue for the second selected row
                      };
                    } else {
                      return {
                        border: "inherit", // Inherit for other rows
                      };
                    }
                  },
                }}
                onRowClick={handleRowClick}
              />
            </TableWrapper>
          </AccordionDetails>
        </Accordion>

        <Box
          style={{
            display: selectedWaterSourcesCompare.length > 0 ? "block" : "none",
          }}
        >
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box pv={4} style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h4">Comparing Water Sources</Typography>
                <PrintRefButton
                  data-html2canvas-ignore
                  ref={compareSaveRef}
                  title="Water Sources Compare"
                />
                <SaveRefButton
                  data-html2canvas-ignore
                  ref={compareSaveRef}
                  title="Water Sources Compare"
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterSourcesCompare([])}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
          <div ref={compareSaveRef}>
            <WaterSourcesCompare
              selectedWaterSourcesCompare={selectedWaterSourcesCompare}
            />
          </div>
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box pv={4} style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h4">Comparing Water Sources</Typography>
                <PrintRefButton
                  data-html2canvas-ignore
                  ref={compareSaveRef}
                  title="Water Sources Compare"
                />
                <SaveRefButton
                  data-html2canvas-ignore
                  ref={compareSaveRef}
                  title="Water Sources Compare"
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterSourcesCompare([])}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>

        <Box
          style={{
            display:
              selectedWaterSource || selectedWaterSource === 0
                ? "block"
                : "none",
          }}
        >
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box pv={4} style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h4">
                  {selectedWaterSource === 0
                    ? "Creating New Water Source"
                    : "Viewing Water Source"}
                </Typography>
                <PrintRefButton
                  data-html2canvas-ignore
                  ref={readSaveRef}
                  title="Water Sources Compare"
                />
                <SaveRefButton
                  data-html2canvas-ignore
                  ref={readSaveRef}
                  title="Water Sources Compare"
                />
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterSource(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
          <div ref={readSaveRef}>
            <BasicWaterSourceInfo
              selectedWaterSource={selectedWaterSource}
              setSelectedWaterSource={setSelectedWaterSource}
              refetchWaterSources={refetch}
              selectedWaterSourcesCompare={selectedWaterSourcesCompare}
            />
          </div>
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box pv={4} style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h4">
                  {selectedWaterSource === 0
                    ? "Creating New Water Source"
                    : "Viewing Water Source"}
                </Typography>
                <PrintRefButton
                  data-html2canvas-ignore
                  ref={readSaveRef}
                  title="Water Sources Compare"
                />
                <SaveRefButton
                  data-html2canvas-ignore
                  ref={readSaveRef}
                  title="Water Sources Compare"
                />
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterSource(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default WaterSourcesManagement;
